'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { CUSTOMER_NOTIFICATIONS_LATEST_LIMIT } from '@/entities/customer-notification/api/customer-notifications/constants';
import { isNewsUnread } from '@/features/news/is-news-unread';
import { getNewsReadIds, useGuestNewsReadIds } from '@/features/news/news-read-storage';
import { openCustomerNotificationToast } from '@/features/news/open-customer-notification';
import { useCustomerNotificationsList } from '@/features/news/useCustomerNotificationsList';
import { usePathname } from '@/shared/i18n/navigation';

export const useNewsAutoShow = () => {
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const skipAutoShow = pathname.startsWith('/editor') || searchParams.get('preview') === 'true';
    const { notificationsQuery, items } = useCustomerNotificationsList({
        limit: CUSTOMER_NOTIFICATIONS_LATEST_LIMIT,
        enabled: !skipAutoShow,
    });
    // Re-run when guest read ids change (e.g. toast closed); check uses getNewsReadIds() in effect
    // so the first paint after hydration does not use an empty SSR snapshot.
    const readIds = useGuestNewsReadIds();
    const shownIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (skipAutoShow) return;
        if (!notificationsQuery.isSuccess || notificationsQuery.isPlaceholderData) return;

        const latest = items[0];
        const readIdsNow = getNewsReadIds();
        if (!latest || !isNewsUnread(latest, { readIds: readIdsNow })) return;
        if (shownIdRef.current === latest.id) return;

        shownIdRef.current = latest.id;
        void openCustomerNotificationToast(latest);
    }, [skipAutoShow, notificationsQuery.isSuccess, notificationsQuery.isPlaceholderData, items, readIds]);
};
